// 261010 Epic 2 / P3c — PANEL hồ sơ phong cách cấp truyện (mẫu `styleSection`): đầu panel · Trải nghiệm khán giả · Luật (≤8) · Nhịp · Màu & ánh sáng.
// Mỗi khối có nút lưu riêng; MỖI nút PUT toàn hồ sơ với `baseRevision` (khối đang lưu lấy từ nháp, các khối khác lấy từ bản đã lưu — `putBody`). Việc gọi API / kiểm / lỗi nằm ở WorkflowStyleSection; ở đây chỉ vẽ + giữ nháp từng khối.
// Khác mẫu (Phase doc §"Khác mẫu"): không "Áp ở Video…", không cột "Đang đạt" / "Video thuộc loại này", không "AI chấm hồ sơ", không "Xem như truyện chưa có hồ sơ", không alert "N plan thành cũ".
import React, { useEffect, useRef, useState } from 'react';
import type { StyleAudience, StyleLook, StyleProfileView, StyleRule } from '../../types/filmStyleProfile';
import {
  BAND_ROWS, BLOCK_SAVE_LABEL, MAX_RULES_TEXT, OWNER_LABEL, OWNER_ORDER, RHYTHM_MODE_LABEL, RHYTHM_NOTE, STYLE_LIMITS, STYLE_BLOCKS,
  canAddRule, contentOf, emptyRuleForm, lookNoteOf, rhythmFromForm, rhythmToForm, ruleFromForm, ruleSentence, ruleThenText, ruleToForm, ruleWhenText, saveNote, withRule, withoutRule,
  type BlockValue, type RhythmForm, type RuleForm, type StyleAction, type StyleBlockKey,
} from './styleProfilePolicy';
import type { RhythmMode, StyleOwner } from '../../types/filmStyleProfile';

interface Props {
  view: StyleProfileView;
  actions: StyleAction[];
  /** Cổng chung của mọi nút ghi (job đang chạy / máy tự chạy / đang gửi). */
  gate: { ok: boolean; reason?: string };
  /** Lỗi bằng chữ đã gom theo khối (`GENERAL` hiện ở đầu panel). */
  errors: Record<StyleBlockKey | 'GENERAL', string[]>;
  onGenerate: () => void;
  onApprove: () => void;
  /** Lưu một khối. Trả `true` khi đã lưu (để khối đóng trình sửa). */
  onSave: (block: StyleBlockKey, value: BlockValue) => Promise<boolean>;
  /** Khối có chỉnh sửa chưa lưu ⇒ nút Duyệt tắt (duyệt chỉ áp bản đã lưu). */
  onDirty: (block: StyleBlockKey, dirty: boolean) => void;
}

const EMPTY_LOOK: StyleLook = { genreTone: '', palette: '', lighting: '', filmLook: '' };

/** Nháp của một khối: nạp lại từ server CHỈ khi giá trị đã lưu của CHÍNH khối đó đổi (lưu khối A không làm mất nháp khối B). */
function useDraft<T>(saved: T): [T, React.Dispatch<React.SetStateAction<T>>, boolean] {
  const key = JSON.stringify(saved);
  const [local, setLocal] = useState<T>(saved);
  const last = useRef(key);
  useEffect(() => { if (last.current !== key) { last.current = key; setLocal(saved); } }, [key, saved]);
  return [local, setLocal, JSON.stringify(local) !== key];
}
function useReportDirty(block: StyleBlockKey, dirty: boolean, onDirty: Props['onDirty']): void {
  const cb = useRef(onDirty); cb.current = onDirty;
  useEffect(() => { cb.current(block, dirty); }, [block, dirty]);
  useEffect(() => () => cb.current(block, false), [block]);
}

const Errs: React.FC<{ lines: string[] }> = ({ lines }) => (lines.length
  ? <ul className="wf-sty-errs" role="alert" data-testid="wf-sty-errs">{lines.map((l, i) => <li key={i}>{l}</li>)}</ul> : null);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="wf-sty-fld"><span className="wf-sty-fl">{label}</span>{children}</label>
);

interface BlockProps { view: StyleProfileView; gate: Props['gate']; lines: string[]; onSave: Props['onSave']; onDirty: Props['onDirty'] }

// ───────────────────────── Trải nghiệm khán giả ─────────────────────────

const AudienceBlock: React.FC<BlockProps> = ({ view, gate, lines, onSave, onDirty }) => {
  const [a, setA, dirty] = useDraft<StyleAudience>(contentOf(view.profile).audience);
  useReportDirty('audience', dirty, onDirty);
  const set = (k: keyof StyleAudience) => (e: React.ChangeEvent<HTMLTextAreaElement>) => setA({ ...a, [k]: e.target.value });
  return (
    <div className="wf-sty-box" data-testid="wf-sty-audience">
      <div className="wf-sty-pt">Trải nghiệm khán giả mong muốn <small>AI đề xuất</small></div>
      <Field label="Khán giả cần cảm thấy gì"><textarea className="wf-sty-fa" rows={2} value={a.feel} onChange={set('feel')} disabled={!gate.ok} aria-label="Khán giả cần cảm thấy gì" /></Field>
      <Field label="Khán giả biết gì, khi nào"><textarea className="wf-sty-fa" rows={2} value={a.know} onChange={set('know')} disabled={!gate.ok} aria-label="Khán giả biết gì, khi nào" /></Field>
      <Field label="Mức hiện thực / cường điệu"><textarea className="wf-sty-fa" rows={2} value={a.realism} onChange={set('realism')} disabled={!gate.ok} aria-label="Mức hiện thực" /></Field>
      <Errs lines={lines} />
      <div className="wf-sty-row">
        <button type="button" className="wf-btn primary" disabled={!gate.ok || !dirty} title={gate.ok ? (dirty ? undefined : 'Chưa có thay đổi để lưu.') : gate.reason} onClick={() => { void onSave('audience', a); }} data-action="STYLE_SAVE_audience">{BLOCK_SAVE_LABEL.audience}</button>
        <span className="wf-sty-note">{saveNote(view.status, 'trải nghiệm')}</span>
      </div>
    </div>
  );
};

// ───────────────────────── Luật ─────────────────────────

const RuleView: React.FC<{ rule: StyleRule; gate: Props['gate']; asking: boolean; onEdit: () => void; onAsk: () => void; onKeep: () => void; onDelete: () => void }> = ({ rule, gate, asking, onEdit, onAsk, onKeep, onDelete }) => (
  <article className="wf-sty-rule" data-testid="wf-sty-rule" data-rule={rule.id}>
    <div className="wf-sty-rule-h">
      <span className="wf-sty-rid">{rule.id}</span><b>{rule.title}</b>
      {rule.owners.map((o) => <span key={o} className="wf-sty-tag">{OWNER_LABEL[o]}</span>)}
      <span className="sp" />
      <button type="button" className="wf-btn" disabled={!gate.ok} title={gate.reason} onClick={onEdit}>Sửa</button>
      {asking
        ? <span className="wf-sty-ask">Xoá luật {rule.id}? <button type="button" className="wf-btn primary" disabled={!gate.ok} onClick={onDelete}>Xoá</button><button type="button" className="wf-btn ghost" onClick={onKeep}>Giữ</button></span>
        : <button type="button" className="wf-btn ghost" disabled={!gate.ok} title={gate.reason} onClick={onAsk}>Xoá</button>}
    </div>
    <p className="wf-sty-rule-s" aria-label={ruleSentence(rule)}><b>Khi</b> {ruleWhenText(rule.when)}, <b>thì</b> {ruleThenText(rule.then)}.</p>
    <dl className="wf-sty-kv">
      <dt>Ngoại lệ</dt><dd>{rule.exception ? rule.exception : <span className="faint">không có</span>}</dd>
      <dt>Tiêu chí kiểm</dt><dd>{rule.check}</dd>
    </dl>
  </article>
);

const RuleEdit: React.FC<{ form: RuleForm; isNew: boolean; status: StyleProfileView['status']; gate: Props['gate']; onChange: (f: RuleForm) => void; onSave: () => void; onCancel: () => void }> = ({ form, isNew, status, gate, onChange, onSave, onCancel }) => {
  const toggle = (o: StyleOwner) => onChange({ ...form, owners: form.owners.includes(o) ? form.owners.filter((x) => x !== o) : [...form.owners, o] });
  return (
    <article className="wf-sty-rule editing" data-testid="wf-sty-rule-edit">
      <div className="wf-sty-rule-h"><span className="wf-sty-rid">{form.id}</span><b>{isNew ? 'Thêm luật' : 'Sửa luật'}</b></div>
      <Field label="Tên luật"><input className="wf-sty-fi" type="text" value={form.title} onChange={(e) => onChange({ ...form, title: e.target.value })} aria-label="Tên luật" /></Field>
      <Field label="Khi (điều kiện)"><textarea className="wf-sty-fa" rows={2} value={form.when} onChange={(e) => onChange({ ...form, when: e.target.value })} aria-label="Khi" /></Field>
      <Field label="Thì (cách làm)"><textarea className="wf-sty-fa" rows={2} value={form.then} onChange={(e) => onChange({ ...form, then: e.target.value })} aria-label="Thì" /></Field>
      <Field label="Ngoại lệ"><textarea className="wf-sty-fa" rows={2} value={form.exception} onChange={(e) => onChange({ ...form, exception: e.target.value })} aria-label="Ngoại lệ" /></Field>
      <Field label="Tiêu chí kiểm"><textarea className="wf-sty-fa" rows={2} value={form.check} onChange={(e) => onChange({ ...form, check: e.target.value })} aria-label="Tiêu chí kiểm" /></Field>
      <div className="wf-sty-fld"><span className="wf-sty-fl">Vai phụ trách (chọn ít nhất một)</span>
        <div className="wf-sty-owners" role="group" aria-label="Vai phụ trách">
          {OWNER_ORDER.map((o) => <label key={o}><input type="checkbox" checked={form.owners.includes(o)} onChange={() => toggle(o)} />{OWNER_LABEL[o]}</label>)}
        </div>
      </div>
      <div className="wf-sty-row">
        <button type="button" className="wf-btn primary" disabled={!gate.ok} title={gate.reason} onClick={onSave} data-action="STYLE_SAVE_rules">{BLOCK_SAVE_LABEL.rules}</button>
        <button type="button" className="wf-btn ghost" onClick={onCancel}>Huỷ</button>
        <span className="wf-sty-note">{saveNote(status, 'luật')}</span>
      </div>
    </article>
  );
};

const RulesBlock: React.FC<BlockProps> = ({ view, gate, lines, onSave, onDirty }) => {
  const rules = contentOf(view.profile).rules;
  const [editing, setEditing] = useState<{ form: RuleForm; isNew: boolean } | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  useReportDirty('rules', editing !== null, onDirty);
  const addable = canAddRule(rules);
  const save = async () => {
    if (!editing) return;
    const next = withRule(rules, ruleFromForm(editing.form));
    if (await onSave('rules', next)) setEditing(null);
  };
  const remove = async (id: string) => { await onSave('rules', withoutRule(rules, id)); setAsking(null); };
  return (
    <div className="wf-sty-box" data-testid="wf-sty-rules">
      <div className="wf-sty-pt">Luật phong cách <small>{rules.length}/{STYLE_LIMITS.maxRules} luật · dạng “khi X thì Y”, mỗi luật có ngoại lệ và tiêu chí kiểm</small></div>
      {rules.length === 0 && !editing ? <p className="muted" style={{ margin: '4px 0 8px' }}>Chưa có luật nào — bấm “AI đề xuất” ở đầu panel hoặc “＋ Thêm luật”.</p> : null}
      {rules.map((r) => (editing && !editing.isNew && editing.form.id === r.id
        ? <RuleEdit key={r.id} form={editing.form} isNew={false} status={view.status} gate={gate} onChange={(form) => setEditing({ form, isNew: false })} onSave={() => { void save(); }} onCancel={() => setEditing(null)} />
        : <RuleView key={r.id} rule={r} gate={gate} asking={asking === r.id} onEdit={() => { setAsking(null); setEditing({ form: ruleToForm(r), isNew: false }); }} onAsk={() => setAsking(r.id)} onKeep={() => setAsking(null)} onDelete={() => { void remove(r.id); }} />))}
      {editing?.isNew
        ? <RuleEdit form={editing.form} isNew status={view.status} gate={gate} onChange={(form) => setEditing({ form, isNew: true })} onSave={() => { void save(); }} onCancel={() => setEditing(null)} /> : null}
      <Errs lines={lines} />
      <div className="wf-sty-row">
        <button type="button" className="wf-btn" disabled={!gate.ok || !addable || editing !== null} title={!gate.ok ? gate.reason : !addable ? MAX_RULES_TEXT : undefined}
          onClick={() => { setAsking(null); setEditing({ form: emptyRuleForm(rules), isNew: true }); }} data-action="STYLE_RULE_ADD">＋ Thêm luật</button>
        {!addable ? <span className="wf-sty-note">{MAX_RULES_TEXT}</span> : null}
      </div>
    </div>
  );
};

// ───────────────────────── Nhịp ─────────────────────────

const RhythmBlock: React.FC<BlockProps> = ({ view, gate, lines, onSave, onDirty }) => {
  const [f, setF, dirty] = useDraft<RhythmForm>(rhythmToForm(contentOf(view.profile).rhythm));
  useReportDirty('rhythm', dirty, onDirty);
  const hasSaved = !!view.profile?.rhythm;
  const setBand = (k: 'air' | 'dialogue' | 'action', side: 'lo' | 'hi') => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, bands: { ...f.bands, [k]: { ...f.bands[k], [side]: e.target.value } } });
  return (
    <div className="wf-sty-box" data-testid="wf-sty-rhythm">
      <div className="wf-sty-pt">Nhịp short drama</div>
      {!hasSaved ? <p className="muted" style={{ margin: '0 0 8px' }}>Chưa có nhịp — nhập ba dải rồi bấm “{BLOCK_SAVE_LABEL.rhythm}”, hoặc để AI đề xuất.</p> : null}
      <div className="wf-sty-row" style={{ marginBottom: 6 }}>
        <b style={{ fontSize: 13 }}>Kiểu nhịp</b>
        <select className="wf-sty-sel" value={f.mode} disabled={!gate.ok} onChange={(e) => setF({ ...f, mode: e.target.value as RhythmMode })} aria-label="Kiểu nhịp">
          {(Object.keys(RHYTHM_MODE_LABEL) as RhythmMode[]).map((m) => <option key={m} value={m}>{RHYTHM_MODE_LABEL[m]}</option>)}
        </select>
        <span className="wf-sty-tag">lựa chọn thiết kế, dùng để chạy</span>
      </div>
      <table className="wf-sty-tb">
        <thead><tr><th>Loại đoạn</th><th>Shot / 10 giây (mục tiêu)</th></tr></thead>
        <tbody>{BAND_ROWS.map((row) => (
          <tr key={row.key}><td>{row.label}</td>
            <td><input className="wf-sty-num" inputMode="decimal" value={f.bands[row.key].lo} onChange={setBand(row.key, 'lo')} disabled={!gate.ok} aria-label={`${row.label} — từ`} /> – <input className="wf-sty-num" inputMode="decimal" value={f.bands[row.key].hi} onChange={setBand(row.key, 'hi')} disabled={!gate.ok} aria-label={`${row.label} — đến`} /></td></tr>))}
        </tbody>
      </table>
      <Field label="Ngoại lệ có căn cứ"><textarea className="wf-sty-fa" rows={2} value={f.exceptions} onChange={(e) => setF({ ...f, exceptions: e.target.value })} disabled={!gate.ok} aria-label="Ngoại lệ có căn cứ của nhịp" /></Field>
      <div className="wf-note">{RHYTHM_NOTE}</div>
      <Errs lines={lines} />
      <div className="wf-sty-row">
        <button type="button" className="wf-btn primary" disabled={!gate.ok || !dirty} title={gate.ok ? (dirty ? undefined : 'Chưa có thay đổi để lưu.') : gate.reason} onClick={() => { void onSave('rhythm', rhythmFromForm(f)); }} data-action="STYLE_SAVE_rhythm">{BLOCK_SAVE_LABEL.rhythm}</button>
        <span className="wf-sty-note">{saveNote(view.status, 'nhịp')}</span>
      </div>
    </div>
  );
};

// ───────────────────────── Màu & ánh sáng ─────────────────────────

const LookBlock: React.FC<BlockProps> = ({ view, gate, lines, onSave, onDirty }) => {
  const [l, setL, dirty] = useDraft<StyleLook>(contentOf(view.profile).look ?? EMPTY_LOOK);
  useReportDirty('look', dirty, onDirty);
  const note = lookNoteOf(view);
  const own = view.planLook;
  const hasOwn = !!(own.genreTone || own.palette || own.lighting || own.filmLook);
  const inp = (k: keyof StyleLook, label: string) => (
    <Field label={label}><input className="wf-sty-fi" type="text" value={l[k]} onChange={(e) => setL({ ...l, [k]: e.target.value })} disabled={!gate.ok} aria-label={label} /></Field>
  );
  return (
    <div className="wf-sty-box" data-testid="wf-sty-look">
      <div className="wf-sty-pt">Màu & ánh sáng <small>Visual Bible, gộp vào hồ sơ phong cách · chỉ áp cho plan tạo mới</small></div>
      {inp('genreTone', 'Genre / tone')}{inp('palette', 'Bảng màu')}{inp('lighting', 'Ánh sáng')}{inp('filmLook', 'Film look')}
      {note ? (
        <div className="wf-note" data-testid="wf-sty-look-own">{note}
          {hasOwn ? <dl className="wf-sty-kv" style={{ marginTop: 6 }}>
            <dt>Genre / tone</dt><dd>{own.genreTone || '—'}</dd><dt>Bảng màu</dt><dd>{own.palette || '—'}</dd><dt>Ánh sáng</dt><dd>{own.lighting || '—'}</dd><dt>Film look</dt><dd>{own.filmLook || '—'}</dd>
          </dl> : null}
        </div>) : null}
      <Errs lines={lines} />
      <div className="wf-sty-row">
        <button type="button" className="wf-btn primary" disabled={!gate.ok || !dirty} title={gate.ok ? (dirty ? undefined : 'Chưa có thay đổi để lưu.') : gate.reason} onClick={() => { void onSave('look', l); }} data-action="STYLE_SAVE_look">{BLOCK_SAVE_LABEL.look}</button>
        <span className="wf-sty-note">{saveNote(view.status, 'màu & ánh sáng')}</span>
      </div>
    </div>
  );
};

// ───────────────────────── panel ─────────────────────────

const WorkflowStyleProfilePanel: React.FC<Props> = ({ view, actions, gate, errors, onGenerate, onApprove, onSave, onDirty }) => {
  const gen = actions.find((a) => a.key === 'STYLE_GENERATE');
  const apr = actions.find((a) => a.key === 'STYLE_APPROVE');
  const profile = view.profile;
  const block: BlockProps = { view, gate, lines: [], onSave, onDirty };
  return (
    <div className="wf-sty-panel" data-testid="wf-sty-panel">
      <div className="wf-sty-box">
        <div className="wf-sty-head-row">
          <b>Hồ sơ phong cách của truyện</b>
          <span className={`wf-chip ${view.status === 'APPROVED' ? 'ok' : view.status === 'DRAFT' ? 'warn' : 'todo'}`}>{view.status === 'APPROVED' ? 'đã duyệt' : view.status === 'DRAFT' ? 'chờ bạn duyệt' : 'chưa có hồ sơ'}</span>
          {profile?.approved ? <span className="wf-sty-tag mono">v{profile.approved.version}</span> : null}
          <span className="wf-sty-tag">cấp truyện, dùng chung mọi tập</span>
          <span style={{ flex: 1 }} />
          {gen?.visible ? <button type="button" className="wf-btn" disabled={!gen.enabled} title={gen.reason} onClick={onGenerate} data-action={gen.key}>{gen.label}</button> : null}
          {apr?.visible ? <button type="button" className="wf-btn primary" disabled={!apr.enabled} title={apr.reason} onClick={onApprove} data-action={apr.key}>{apr.label}</button> : null}
        </div>
        <p className="faint" style={{ margin: '8px 0 0', fontSize: 13 }}>
          Làm một lần cho cả truyện, gộp cả Visual Bible (màu & ánh sáng). Hồ sơ là nguồn luật cho các bước sau; ở bản này chưa bước nào đọc hồ sơ nên sửa hồ sơ chưa làm bước nào thành “cũ”.
          AI đề xuất là lượt chữ, không hỏi xác nhận; bạn cần duyệt hồ sơ khi truyện chưa có.
        </p>
        <Errs lines={errors.GENERAL} />
      </div>
      {STYLE_BLOCKS.map((k) => {
        const props = { ...block, lines: errors[k] };
        return k === 'audience' ? <AudienceBlock key={k} {...props} /> : k === 'rules' ? <RulesBlock key={k} {...props} /> : k === 'rhythm' ? <RhythmBlock key={k} {...props} /> : <LookBlock key={k} {...props} />;
      })}
    </div>
  );
};

export default WorkflowStyleProfilePanel;
